import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "../utils/toast";
import { withApiError, withApiErrorResponse, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;
export const fetchUserList = async (
  currentPage,
  pageSize,
  filters,
  searchTerm,
  searchAreacodeTerm,
  selectedOrgId,
  projectId
) => {
  const token = Cookies.get("accessToken");
  const userType = Cookies.get("userType");
  let userTypeFilter = "";
  if (filters.userType && filters.userType !== "Everything") {
    userTypeFilter = `&userType=${filters.userType}`;
  }

  const searchQuery = searchTerm
    ? `&userName=${encodeURIComponent(searchTerm)}`
    : "";
  const areaCodeQuery = searchAreacodeTerm
    ? `&areaCode=${encodeURIComponent(searchAreacodeTerm)}`
    : "";

  let organizationQuery = "";
  // if (userType === "super_admin" && selectedOrgId) {
  organizationQuery = `&organizationId=${selectedOrgId}`;
  // }

  try {
    const response = await axios.get(
      `${baseURL}api/v1/user/get-users?pageNo=${currentPage}&size=${pageSize}&projectIds=${projectId}${organizationQuery}${areaCodeQuery}${userTypeFilter}${searchQuery}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

const userGridHeaders = () => ({
  Authorization: `Bearer ${Cookies.get("accessToken")}`,
  "Content-Type": "application/json",
  "X-App-Type": "WEB",
});

// Error carrying the backend's own message (validation errors such as an
// unknown column/operator, a bad number or missing projectIds come back with a
// readable body.error/body.value rather than a generic "Server error").
export class UserGridError extends Error {
  constructor(code, message) {
    super(message || `User list request failed (code ${code})`);
    this.name = "UserGridError";
    this.code = code;
  }
}

export const USER_SORTABLE_COLUMNS = new Set(["name", "email", "phone", "employeeId", "userType", "userStatus", "organization"]);

const toIdArray = (ids) =>
  (Array.isArray(ids) ? ids : String(ids ?? "").split(","))
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

const throwUserGridError = (error) => {
  if (axios.isCancel(error) || error?.name === "CanceledError" || error?.name === "AbortError") throw error;
  const data = error?.response?.data;
  if (data) throw new UserGridError(data?.header?.code ?? error?.response?.status, apiErrorMessage(data) || undefined);
  throw error;
};

/**
 * POST api/v1/user/get-users -- the server-side filtered/sorted/paged user list.
 * Same response as the GET version; totalNumberOfUsers is the filtered count.
 * columnFilters use the task-list grid format ({ column, op, values|value|from/to }).
 */
export const searchUsers = async ({
  page = 0,
  size = 10,
  projectIds,
  organizationId,
  departmentIds,
  search,
  sortBy,
  sortOrder,
  columnFilters,
  signal,
}) => {
  const body = { page, size, projectIds: toIdArray(projectIds) };
  const orgId = Number(organizationId);
  if (Number.isFinite(orgId) && orgId > 0) body.organizationId = orgId;
  if (departmentIds?.length) body.departmentIds = toIdArray(departmentIds);
  if (String(search ?? "").trim()) body.search = String(search).trim();
  // Only these columns can be sorted; anything else is rejected with 601.
  if (sortBy && USER_SORTABLE_COLUMNS.has(sortBy)) {
    body.sortBy = sortBy;
    body.sortOrder = sortOrder === "desc" ? "desc" : "asc";
  }
  if (columnFilters?.length) body.columnFilters = columnFilters;

  try {
    const response = await axios.post(`${baseURL}api/v1/user/get-users`, body, {
      headers: userGridHeaders(),
      signal,
    });
    const code = response.data?.header?.code;
    const value = response.data?.body?.value;
    if (Number(code) === 600) {
      return {
        userList: Array.isArray(value?.userList) ? value.userList : [],
        totalNumberOfUsers: Number(value?.totalNumberOfUsers ?? value?.userList?.length ?? 0),
      };
    }
    throw new UserGridError(code, apiErrorMessage(response.data) || undefined);
  } catch (error) {
    if (error instanceof UserGridError) throw error;
    return throwUserGridError(error);
  }
};

/**
 * POST api/v1/user/get-users/column-values -- distinct values for one column's
 * filter dropdown. "" means blank (e.g. "no group"). Any filter on the
 * requested column itself is ignored server-side.
 *
 * Resolves to string[]: the stored values exactly as the server sent them, to
 * be sent back unchanged in an `in` filter (userType enum names, userStatus
 * "true"/"false", group/project/department/workflow/role names). The default
 * limit is 200 (max 1000); `search` looks past that cap. lastLogin always
 * returns [] because it only supports a date range.
 *
 * Backend limitation: the picker does not take the toolbar search, userType,
 * userName or areaCode, so it can list values for users the grid is hiding.
 */
export const fetchUserColumnValues = async ({
  projectIds,
  organizationId,
  departmentIds,
  column,
  search,
  limit = 200,
  columnFilters,
  signal,
}) => {
  const body = {
    projectIds: toIdArray(projectIds),
    column,
    limit: Math.min(Math.max(Number(limit) || 200, 1), 1000),
  };
  const orgId = Number(organizationId);
  if (Number.isFinite(orgId) && orgId > 0) body.organizationId = orgId;
  if (departmentIds?.length) body.departmentIds = toIdArray(departmentIds);
  if (String(search ?? "").trim()) body.search = String(search).trim();
  if (columnFilters?.length) body.columnFilters = columnFilters;

  try {
    const response = await axios.post(`${baseURL}api/v1/user/get-users/column-values`, body, {
      headers: userGridHeaders(),
      signal,
    });
    if (Number(response.data?.header?.code) !== 600) {
      throw new UserGridError(response.data?.header?.code, apiErrorMessage(response.data) || undefined);
    }
    const raw = response.data?.body?.value ?? [];
    const list = Array.isArray(raw) ? raw : raw?.values || [];
    const seen = new Set();
    return list
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item !== "object") return String(item);
        // Defensive: the details columns filter by name, so an object entry
        // contributes its value/name, never its id.
        return String(item.value ?? item.name ?? item.label ?? "");
      })
      .filter((value) => !seen.has(value) && seen.add(value));
  } catch (error) {
    if (error instanceof UserGridError) throw error;
    return throwUserGridError(error);
  }
};

/**
 * GET api/v1/user/get-users/columns -- the grid's column list:
 * [{ key, label, filterable, accessible }]. Super admins may pass a userType to
 * read another user type's columns.
 */
export const fetchUserGridColumns = async (userType) => {
  try {
    const response = await axios.get(`${baseURL}api/v1/user/get-users/columns`, {
      headers: userGridHeaders(),
      ...(userType ? { params: { userType } } : {}),
    });
    if (Number(response.data?.header?.code) !== 600) {
      throw new UserGridError(response.data?.header?.code, apiErrorMessage(response.data) || undefined);
    }
    const value = response.data?.body?.value;
    return Array.isArray(value) ? value : [];
  } catch (error) {
    if (error instanceof UserGridError) throw error;
    return throwUserGridError(error);
  }
};

export const updateUserStatus = async (userId, status) => {
  const token = Cookies.get("accessToken");
  const userIdList = [{ userId, status }];
  try {
    const response = await axios.post(
      `${baseURL}api/v1/user/update-status`,
      { userIdList },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.header.code === 600) {
      return true;
    } else {
      const backendError = new Error("Failed to update user status");
      backendError.body = response.data.body;
      throw backendError;
    }
  } catch (error) {
    console.error("Error updating user status:", error.message);
    throw withApiErrorResponse(error);
  }
};

export const createOrganization = async (orgName, logoUrl, reportEmails = []) => {
  const accessToken = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/organization/create`,
      { organizationName: orgName, logo: logoUrl, reportEmails: Array.isArray(reportEmails) ? reportEmails : [] },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error creating organization:", error);
    throw withApiErrorResponse(error);
  }
};

export const fetchOrganizations = async (accessToken) => {
  try {
    const response = await axios.get(
      `${baseURL}api/v1/organization/get-list`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!response || !response.data || response.data.header.code !== 600) {
      throw new Error("Failed to fetch organizations");
    }
    return response.data.body.value;
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw withApiErrorResponse(error);
  }
};



export const getUserInfoAPI = (userId, customToken = null) => {
  const token = customToken || Cookies.get("accessToken");
  return axios.get(
    `${baseURL}api/v1/user/user-detail`,
    {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};
export const createUser = async (userData, token) => {
  try {
    const response = await axios.post(
      `${baseURL}api/v1/user/create`,
      userData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error creating user:", error);
    throw withApiErrorResponse(error);
  }
};
/**
 * Update user profile.
 * PUT api/v1/user/update-profile
 * Payload: same as create (name, email, phone, userType, areaCode, userStatus, organization, departmentList, projectList, workflowList, roleIds) + userId
 */
export const updateUser = async (userData, token) => {
  try {
    const response = await axios.put(
      `${baseURL}api/v1/user/update-profile`,
      userData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error updating user:", error);
    throw withApiErrorResponse(error);
  }
};
export const updateProfileInfo = async (userId, name, email, phone, password, isPasswordCleared,) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.put(
      `${baseURL}api/v1/user/update-profile`,
      {
        userId,
        name,
        email,
        phone,
        password,
        passwordChanged: isPasswordCleared
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};
//get userinfo
export const getUserInfo = async () => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/user/user-detail`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

//upload profile picture
export const updateProfilePicture = async (url) => {
  console.log(url)
  const token = Cookies.get("accessToken");

  try {
    const userId = Cookies.get("userId");
    const response = await axios.post(
      `${baseURL}api/v1/user/profile-pic`,
      {
        userId,
        profilePic: url,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    let profilePic = Cookies.get("profilePic");

    if (profilePic) {
      Cookies.remove("profilePic");
    }

    Cookies.set("profilePic", url);
    return withApiError(response.data);
  } catch (error) {
    console.error(
      "Error updating profile picture:",
      error.response ? error.response.data : error
    );
    throw withApiErrorResponse(error);
  }
};

//get list of orgnization
export const fetchOrgnizationData = async (currentPage, pageSize) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/organization/list/get-all?page=${currentPage}&size=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

//get org info by id
export const fetchOrganizationById = async (orgnizationId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/organization/get?id=${orgnizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching organization data:", error);
    throw withApiErrorResponse(error);
  }
};

// delete organization (soft delete)
export const deleteOrganization = async (organizationId) => {
  const token = Cookies.get("accessToken");
  if (!organizationId && organizationId !== 0) {
    throw new Error("organizationId is mandatory");
  }
  const cleanId = String(organizationId).replace(/"/g, "");
  try {
    const response = await axios.delete(
      `${baseURL}api/v1/organization/delete?organizationId=${cleanId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.data?.header?.code === 600) {
      return withApiError(response.data);
    }
    throw new Error(apiErrorMessage(response.data, "Failed to delete organization"));
  } catch (error) {
    console.error("Error deleting organization:", error);
    throw withApiErrorResponse(error);
  }
};
//update org
export const UpdateOrganizationData = async (
  editOrganizationname,
  newLogoUrl,
  orgnizationId,
  reportEmails = []
) => {
  const accessToken = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/organization/create`,
      {
        organizationId: orgnizationId,
        organizationName: editOrganizationname,
        logo: newLogoUrl,
        reportEmails: Array.isArray(reportEmails) ? reportEmails : [],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return withApiError(response.data);
  } catch (error) {
    console.error("Error creating organization:", error);
    throw withApiErrorResponse(error);
  }
};


// get all organization list 
export const getAllByOrganization = async () => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/organization/get-list`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// get all organization list by id
export const getAllByOrganizationId = async (organizationId) => {
  const token = Cookies.get("accessToken");

  if (!organizationId) {
    throw new Error("organization is mandatory");
  }
  const cleanId = String(organizationId).replace(/"/g, "");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/department/get-all?organizationId=${cleanId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// get project list by department id
export const getProjectListByDepartmentId = async (departmentId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/project/get-all-by-departments?departmentIds=${departmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// get workflow field values (returns fieldKeyOptions + valueOptions + rawFields for per-field values)
export const getWorkflowFieldValues = async (workflowId) => {
  const token = Cookies.get("accessToken");
  if (!workflowId) return { fieldKeyOptions: [], valueOptions: [], rawFields: [] };
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-field-values`,
      {
        params: { workflowId },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (response.data?.header?.code !== 600 || !response.data?.body?.value) {
      return { fieldKeyOptions: [], valueOptions: [], rawFields: [] };
    }
    const list = Array.isArray(response.data.body.value) ? response.data.body.value : [];
    const rawFields = list.map((item) => ({
      fieldName: item.fieldName,
      uniqueKey: item.uniqueKey,
      values: Array.isArray(item.values) ? item.values.map((v) => String(v).trim()).filter(Boolean) : [],
    }));
    const fieldKeyOptions = rawFields.map((item) => ({
      value: item.uniqueKey,
      label: item.fieldName || item.uniqueKey || "",
    })).filter((o) => o.value);
    const valuesSet = new Set();
    rawFields.forEach((item) => item.values.forEach((v) => valuesSet.add(v)));
    const valueOptions = Array.from(valuesSet).map((v) => ({ value: v, label: v }));
    return { fieldKeyOptions, valueOptions, rawFields };
  } catch (err) {
    console.error("Error fetching workflow field values:", err);
    return { fieldKeyOptions: [], valueOptions: [], rawFields: [] };
  }
};

// get activity list by project id
export const getActivityListByProjectId = async (projectId) => {
  const token = Cookies.get("accessToken");
  if (!projectId) {
    throw new Error("Project ID is mandatory");
  }
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-activities?projectId=${projectId}`, // update end point by harshit 23-12-2025
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// create deparment
export const getDeparment = async (payload) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.post(
      `${baseURL}api/v1/department/create`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.header.code === 600) {
      toast.success(response.data.body.value)
    }
    else {
      toast.error(apiErrorMessage(response.data))
    }
    return response.data.body;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// update department (same API as create; payload: { id, organizationId, name })
export const updateDepartment = async (payload) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/department/create`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.header.code === 600) {
      toast.success(response.data.body.value);
    } else {
      toast.error(apiErrorMessage(response.data));
    }
    return withApiError(response.data);
  } catch (error) {
    console.error("Error updating department:", error);
    throw withApiErrorResponse(error);
  }
};

// create project
export const getProject = async (payload) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.post(
      `${baseURL}api/v1/project/create`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.header.code === 600) {
      toast.success(response.data.body.value)
    }
    else {
      toast.error(apiErrorMessage(response.data))
    }
    return response.data.body.value;

  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};


// update project
export const updateProject = async (projectId, payload) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/project/update`,
      { projectId, ...payload },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.data?.header?.code === 600) {
      toast.success(response.data?.body?.value ?? "Project updated");
    } else {
      toast.error(apiErrorMessage(response.data, "Failed to update project"));
    }
    return withApiError(response.data);
  } catch (error) {
    console.error("Error updating project:", error);
    throw withApiErrorResponse(error);
  }
};

// get project by id (full details including DCRC config)
export const getProjectById = async (projectId) => {
  const token = Cookies.get("accessToken");
  if (!projectId) return null;
  try {
    const response = await axios.get(
      `${baseURL}api/v1/project/get`,
      {
        params: { projectId },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (response.data?.header?.code === 600 && response.data?.body?.value) {
      const value = response.data.body.value;
      return Array.isArray(value) ? value[0] : value;
    }
    return null;
  } catch (err) {
    console.error("Error fetching project by id:", err);
    return null;
  }
};

// Get per-project auto-assign status (null → false)
export const getAutoAssignStatus = async (projectId) => {
  const token = Cookies.get("accessToken");
  if (!projectId) return false;
  try {
    const response = await axios.get(
      `${baseURL}api/v1/project/get-auto-assign-status`,
      {
        params: { projectId },
        headers: {
          Authorization: `Bearer ${token}`,
          "X-App-Type": "WEB",
        },
      }
    );
    if (response.data?.header?.code === 600) {
      return Boolean(response.data?.body?.value?.isAutoAssignEnabled);
    }
    return false;
  } catch (error) {
    console.error("Error fetching auto-assign status:", error);
    throw withApiErrorResponse(error);
  }
};

// Enable / disable auto-assign for a project
export const updateAutoAssignStatus = async (projectId, isAutoAssignEnabled) => {
  const token = Cookies.get("accessToken");
  if (!projectId) {
    throw new Error("projectId is required");
  }
  try {
    const response = await axios.post(
      `${baseURL}api/v1/project/update-auto-assign-status`,
      { projectId, isAutoAssignEnabled: Boolean(isAutoAssignEnabled) },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-App-Type": "WEB",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error updating auto-assign status:", error);
    throw withApiErrorResponse(error);
  }
};

// all project

export const getProjectList = async (departmentId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/project/get-all?departmentId=${departmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};


// get multiple project 


export const getMultipleProject = async (departmentId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/project/get-all-by-departments?departmentIds=${departmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// get groups for a department (used by the field-agent group picker in AddUser)
export const getGroupsByDepartment = async (departmentId) => {
  const token = Cookies.get("accessToken");

  if (!departmentId && departmentId !== 0) {
    return [];
  }

  try {
    const response = await axios.get(
      `${baseURL}api/v1/group-by-department/get-by-department?departmentId=${departmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value ?? [];
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// create a new group for a department (used by the "create on the fly" option in AddUser)
export const createGroup = async ({ groupName, departmentId }) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.post(
      `${baseURL}api/v1/group-by-department/create`,
      { groupName, departmentId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// get multiple Activity (by multiple project IDs)
export const getMultipleActivity = async (projectIds) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-list-by-projects?projectIds=${projectIds}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

// get workflow list by single project ID (for activity dropdown)
export const getWorkflowListByProjectId = async (projectId) => {
  const token = Cookies.get("accessToken");
  if (!projectId) return [];
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-list?projectId=${projectId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const value = response.data?.body?.value;
    return Array.isArray(value) ? value : [];
  } catch (error) {
    console.error("Error fetching workflow list by project:", error);
    throw withApiErrorResponse(error);
  }
};

// update project status (DCRC enable/disable)
export const updateProjectStatus = async (projectId, isDcrcEnabled) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/project/update-status`,
      { projectId, isDcrcEnabled },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.data?.header?.code === 600) {
      return withApiError(response.data);
    }
    throw new Error(apiErrorMessage(response.data, "Failed to update project status"));
  } catch (error) {
    console.error("Error updating project status:", error);
    throw withApiErrorResponse(error);
  }
};

// update project delete status (soft delete)
export const updateProjectDeleteStatus = async (projectId, isDeleted) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/project/update-delete-status`,
      { projectId, isDeleted },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.data?.header?.code === 600) {
      return withApiError(response.data);
    }
    throw new Error(apiErrorMessage(response.data, "Failed to update project delete status"));
  } catch (error) {
    console.error("Error updating project delete status:", error);
    throw withApiErrorResponse(error);
  }
};

// projects-for-clock-in

export const projectsForClockIn = async (projectIds) => {
  const token = Cookies.get('accessToken');
  try {
    const response = await axios.get(`${baseURL}api/v1/project/get/projects-for-clock-in?projectIds=${projectIds}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response || !response.data || !response.data.header || response.data.header.code !== 600) {
      throw new Error('Failed to fetch user details');
    }

    return response.data.body.value;
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw withApiErrorResponse(error);
  }
};
